import React, { useState } from 'react';
import LocalizedMessage from 'react-l10n';
import OrderList from './OrderList';
import { getTranslation } from '../lang/main';
import useManualReactTableState from './ManualReactTableState';
import { ALL_ORDER_INVOICE_STATUSES } from '../selectors/orderSelector';
import {currencyDisplay, currencyObject, handleAxiosErrors} from '../_utils';
import InlineFieldGroup from './fields/InlineFieldGroup';
import FieldSelect from './fields/FieldSelect';
import { axiosUpdateInvoiceStatus } from '../actions/invoiceActions';

const statusDropdown = (statuses, ignoreStatus) => {
    return statuses
        .filter((item) => item != ignoreStatus)
        .map((item) => {
            return {
                label: item,
                value: item,
            };
        });
};

const orderInvoiceStatusDropdown = statusDropdown(ALL_ORDER_INVOICE_STATUSES, '');

function CustomerOrdersOnAccount(props) {
    const [
        recentCustomerOrdersWatcher,
        setRecentCustomerOrdersWatcher,
    ] = useState(0);

    const mrtState = useManualReactTableState({});

    // User Input
    const { isUserInputLoading, toggleUserInputLoading } = mrtState;
    const { userInput, clearUserInput, updateUserInput } = mrtState;

    // Request
    const saveOrderStatus = (identifier, key, value) => {
        toggleUserInputLoading(identifier, key, true);

        return axiosUpdateInvoiceStatus(identifier, value)
            .then((response) => {
                const { data } = response;

                if (typeof data.data.updateInvoiceStatus != 'undefined') {
                    setRecentCustomerOrdersWatcher(
                        recentCustomerOrdersWatcher + 1
                    );
                }

                toggleUserInputLoading(identifier, key, false);

                // Clear user input
                clearUserInput(identifier, key);
            })
            .catch((error) => {
                handleAxiosErrors(error);
                toggleUserInputLoading(identifier, key, false);
            });
    };

    // Functions

    /**
     * Get order detail link
     *
     * @param row
     * @returns {*}
     */
    const getOrderDetailLink = (row) => {
        return `${window.baseURL}checkout/confirmation/${row.original.ID}/?type=store-account`;
    };

    const getRecentCustomerOrdersCallback = () => {
        return {
            updateListParams: (listParams) => {
                listParams['filters']['View'] = 'invoice_payment';
                listParams['filters']['OrderInvoiceStatus'] = 'Unpaid';
                // listParams['filters']['PaymentMethod'] = 'order-account';

                return listParams;
            },
            updateViewOrderParamsCallback: (listParams) => {
                listParams['filters']['View'] = 'invoice_payment';
                listParams['filters']['OrderInvoiceStatus'] = 'Unpaid';
                // listParams['filters']['PaymentMethod'] = 'order-account';

                return listParams;
            },
            updateTableProps: (
                tableProps,
                { loadOrder, toggleViewOrderModal, toggleRefineResultsModal }
            ) => {
                tableProps.list.columns = [
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading hidden-xs'>
                                        <LocalizedMessage id={'ORDER_NUMBER'} />
                                    </span>
                                    <span className='react-table__heading visible-xs'>
                                        <LocalizedMessage id={'ORDER_NO'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        <a href={getOrderDetailLink(row)}>
                                            {row.value}
                                        </a>
                                    </span>
                                </React.Fragment>
                            );
                        },
                        minWidth: undefined,
                        accessor: 'InternalItemID',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'DELIVER_TO'} />
                                    </span>
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            const { BusinessName, FirstName, Surname } = row.original;

                            if(BusinessName) {
                                return (
                                    <React.Fragment>
                                        <span className='react-table__info'>
                                            {BusinessName}
                                        </span>
                                    </React.Fragment>
                                );
                            }

                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {`${FirstName} ${Surname}`}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        sortable: false,
                        minWidth: undefined,
                        accessor: 'FirstName',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'SOLD_BY'} />
                                    </span>
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            const { BusinessName, FirstName, Surname } = row.value;

                            if(BusinessName) {
                                return (
                                    <React.Fragment>
                                        <span className='react-table__info'>
                                            {BusinessName}
                                        </span>
                                    </React.Fragment>
                                );
                            }

                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {`${FirstName} ${Surname}`}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        sortable: false,
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        minWidth: undefined,
                        accessor: 'ParentMember',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'DATE_ORDERED'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.original.PlacedNice}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        minWidth: undefined,
                        accessor: 'Placed',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading hidden-xs'>
                                        <LocalizedMessage id={'ORDER_STATUS'} />
                                    </span>
                                    <span className='react-table__heading visible-xs'>
                                        <LocalizedMessage id={'STATUS'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        width: 100,
                        accessor: 'Status',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'NO_OF_ARTICLES'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        minWidth: undefined,
                        accessor: 'BoxPackerParcelCount',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'PAYMENT_STATUS'} />
                                    </span>
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            const orderInvoice = row.original.OrderInvoice;

                            const identifier = orderInvoice.ID;
                            const inputKey = 'Status';
                            let hasInlineEdit = false;
                            let currentValue = orderInvoice.Status;

                            if (typeof row.value == 'undefined' || row.value === null) {
                                return '';
                            }

                            if (typeof userInput[identifier] != 'undefined' && typeof userInput[identifier][inputKey] != 'undefined') {
                                hasInlineEdit = currentValue != userInput[identifier][inputKey];
                                currentValue = userInput[identifier][inputKey];
                            }

                            return (
                                <React.Fragment>
                                    <span className='react-table__info react-table__info--default'>
                                        <InlineFieldGroup
                                            hasUndo={true}
                                            isModified={hasInlineEdit}
                                            saveButtonLoading={isUserInputLoading(identifier, inputKey)}
                                            onSaveClick={() => saveOrderStatus(identifier, inputKey, currentValue)}
                                            onUndoClick={() => clearUserInput(identifier, inputKey)}
                                        >
                                            <FieldSelect
                                                label={getTranslation('STATUS')}
                                                animatedLabel={getTranslation('STATUS')}
                                                value={currentValue}
                                                name={inputKey}
                                                options={orderInvoiceStatusDropdown}
                                                onChange={(e) => updateUserInput(identifier, e.target.name, e.target.value)}
                                            />
                                        </InlineFieldGroup>
                                    </span>
                                </React.Fragment>
                            );
                        },
                        sortable: false,
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        width: 175,
                        accessor: 'OrderInvoice',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'TOTAL'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            const { CurrencyDisplayCode, CurrencyFormat } = row.original;
                            const { locale } = currencyObject;

                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {currencyDisplay(row.value, CurrencyDisplayCode, CurrencyFormat, locale)}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        minWidth: undefined,
                        accessor: 'Total',
                    },
                    {
                        expander: true,
                        Expander: ({ isExpanded, ...rest }) => {
                            return (
                                <React.Fragment>
                                    <button
                                        type='button'
                                        className='react-table__extra-data-toggle'
                                    >
                                        <span className='icon icon-chevron-thick' />
                                    </button>
                                </React.Fragment>
                            );
                        },
                        headerClassName: 'visible-xs',
                        className: 'visible-xs',
                        width: 45,
                        sortable: false,
                        accessor: 'ID',
                    },
                ];
                tableProps.list.getTrGroupProps = (state, row, col, instance) => {
                    const { viewIndex } = row;

                    const isExpanded =
                        state.expanded &&
                        state.expanded.hasOwnProperty(viewIndex) &&
                        state.expanded[viewIndex];

                    let classes = 'react-table__row react-table__row--selectable';
                    classes += ` ${isExpanded ? 'react-table__row--expanded' : ''}`;

                    return {
                        className: classes,
                    };
                };

                return tableProps;
            },
        };
    };

    return (
        <React.Fragment>
            <div className='vspace'>
                <OrderList
                    className='card--stackable'
                    heading={getTranslation(
                        'CUSTOMER_ORDERS.ON_ACCOUNT.HEADING'
                    )}
                    refineResultsModalKey='refine-results-customer-orders-recent-orders-list'
                    viewOrderModalKey='view-customer-order-recent-orders-modal'
                    callback={getRecentCustomerOrdersCallback()}
                    requestPropWatcher={recentCustomerOrdersWatcher}
                    showStatus='ShippingStatus'
                    sortBy='Placed'
                    sortDirection='DESC'
                />
            </div>
        </React.Fragment>
    );
}

export default CustomerOrdersOnAccount;
